import React, { useMemo } from "react";
import { Card, Empty, ErrorState, Kpi, PageHeading, Pill, Rate, RowsSkeleton, Table } from "../ui";
import { BarList, SegmentBar, StackedBars } from "../charts";
import { dayStart, toDay, useFilters } from "../filters";
import { useView } from "../data";
import { fmt, pct, statusOf, words } from "../format";

/** Where a suggestion was offered (the `at` label). */
const AT_LABELS = { plan: "Plan card", change: "After a change", confirm: "On the filter card", zero: "After nothing was found" };

/** Why Auto picked a model (the `rule` label). */
const RULE_LABELS = {
  all_filters: "Every criterion is a filter",
  signals: "A criterion only research can search",
  volume: "Asked: volume vs. a research-only criterion",
  segment: "A search on a segment's companies",
  list: "A file list or a segment",
  user: "Picked by the user on the route card",
  fallback: "No rule applied",
};

/** One colour per model, the same on every chart. Auto is a mode, not a model, so it has none here. */
const MODEL_COLORS = { prospeo: "var(--aa-s2)", perplexity: "var(--aa-s3)", manus: "var(--aa-s4)", hp: "var(--aa-s5)" };
/**
 * A model's colour; a switch side (`auto:<model>`) takes its model's, and
 * "auto" alone (Auto before it ran anything) the mode's.
 */
const colorOf = (pin) => {
  const model = String(pin ?? "").startsWith("auto:") ? String(pin).slice(5) : pin;
  return MODEL_COLORS[model] ?? (model === "auto" ? MODE_COLORS.auto : "var(--aa-neutral)");
};

/** Who picked the model: Auto, or the user in the picker. */
const MODE_COLORS = { auto: "var(--aa-s1)", manual: "var(--aa-muted)" };
const MODE_SERIES = [
  { key: "auto", label: "Auto", color: MODE_COLORS.auto },
  { key: "manual", label: "Picked by the user", color: MODE_COLORS.manual },
];

const DAY = 86_400_000;

/** The Conversations page's own filters (`c.*`), cleared when a number opens it. */
const CLEAR_CONVERSATION_FILTERS = Object.fromEntries(
  ["q", "lastStatus", "failed", "feedback", "channel", "originPage", "source", "minRequests", "judged", "why", "created", "sort"].map((k) => [`c.${k}`, null]),
);

/**
 * The discovery agent models, as MODE and MODEL: who picks the model (Auto
 * or the user), which models run and for whom, whether each returns results,
 * how often people change the picker and whether the change found them
 * anything, how Auto routes, and the suggestions between models. Auto
 * is never a model here — under it, a message counts on the model it ran.
 * Every number opens its conversations.
 *
 * From `/obs/v1/models`. Modes, models, changes and list sizes come from our
 * own tables (full history); searches, routing, suggestions and judged
 * outcomes from Mastra signals (from the deploy that records them).
 */
export default function Models() {
  const { params, set } = useFilters();
  const { data, loading, error } = useView("models", params);
  const models = data?.models ?? [];
  const picked = data?.picked ?? [];
  const modes = data?.modes ?? [];
  const autoUnrouted = data?.autoUnrouted ?? 0;
  const changes = data?.changes ?? null;
  const routing = data?.routing ?? [];
  const suggestions = data?.suggestions ?? [];
  const switches = data?.switches ?? [];
  const buckets = data?.sizeBuckets ?? [];
  const suggested = suggestions.reduce((n, r) => n + r.suggested, 0);
  const accepted = suggestions.reduce((n, r) => n + r.accepted, 0);
  const totalSearches = models.reduce((n, x) => n + x.searches, 0);
  // A number opens the Conversations page on exactly its conversations — the
  // list's other filters cleared, so nothing left over narrows it further.
  const open = (what, details, title) =>
    set({
      tab: "conversations",
      ...CLEAR_CONVERSATION_FILTERS,
      "c.model": what,
      "c.modelOf": new URLSearchParams(details).toString(),
      "c.modelTitle": title,
    });

  const daily = useMemo(() => fillPickedDays(data?.pickedDaily, params.from, params.to), [data?.pickedDaily, params.from, params.to]);
  const modesDaily = useMemo(() => fillPickedDays(data?.modesDaily, params.from, params.to), [data?.modesDaily, params.from, params.to]);
  const series = picked.map((p) => ({ key: p.pin, label: p.label, color: colorOf(p.pin) }));
  const modeOf = (mode) => modes.find((m) => m.mode === mode) ?? null;

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="Agent models">
        Who picks the model (Auto or the user), which models run, whether each one finds results, how often people change the picker and
        what the change found, how Auto routes, and the models we suggest. Click any number to see its conversations.
      </PageHeading>

      {error ? <ErrorState error={error} /> : null}

      {/* ── Mode: who picks the model ────────────────────────────────────── */}
      <section style={{ display: "grid", gap: 10 }}>
        <GroupLabel title="Mode" hint="Who picked the model a message ran on" />
        <div className="aa-grid aa-grid-kpi">
          {loading
            ? [0, 1].map((i) => <Kpi key={i} loading />)
            : MODE_SERIES.map((m) => {
                const row = modeOf(m.key);
                return (
                  <Kpi
                    key={m.key}
                    label={m.label}
                    value={pct(row?.share ?? 0)}
                    foot={row ? `of messages · ${fmt(row.users)} ${row.users === 1 ? "person" : "people"}` : "no messages"}
                    onClick={row ? () => open("mode", { mode: m.key }, `${m.label} · conversations`) : undefined}
                  />
                );
              })}
        </div>
      </section>

      {/* ── Model: which model ran ─────────────────────────────────────────── */}
      <section style={{ display: "grid", gap: 10 }}>
        <GroupLabel title="Model" hint="The model a message ran on, whoever picked it" />
        <div className="aa-grid aa-grid-kpi">
          {loading
            ? [0, 1, 2, 3].map((i) => <Kpi key={i} loading />)
            : picked.map((p) => (
                <Kpi
                  key={p.pin}
                  label={p.label}
                  value={pct(p.share)}
                  foot={p.messages ? `of messages · ${fmt(p.users)} ${p.users === 1 ? "person" : "people"}` : "no messages"}
                  onClick={p.messages ? () => open("pin", { pin: p.pin }, `${p.label} · conversations`) : undefined}
                />
              ))}
        </div>
      </section>

      <Card
        title="Who picks the model"
        subtitle="Every message, by whether Auto or the user chose the model it ran on. Full history."
        flush
      >
        {loading ? (
          <RowsSkeleton rows={2} />
        ) : modes.length ? (
          <>
            <div style={{ padding: "16px 20px 4px" }}>
              <StackedBars rows={modesDaily} series={MODE_SERIES} height={180} />
            </div>
            <Table
              defaultSort={{ key: "messages", dir: "desc" }}
              columns={[
                { key: "label", label: "Mode", render: (m) => <ModeName mode={m.mode} label={m.label} /> },
                { key: "messages", label: "Messages", align: "right", render: (m) => fmt(m.messages) },
                { key: "share", label: "Share", align: "right", render: (m) => pct(m.share, 1) },
                {
                  key: "conversations",
                  label: "Conversations",
                  align: "right",
                  render: (m) => <Count n={m.conversations} onClick={() => open("mode", { mode: m.mode }, `${m.label} · conversations`)} />,
                },
                { key: "users", label: "People", align: "right", render: (m) => fmt(m.users) },
                { key: "accounts", label: "Workspaces", align: "right", render: (m) => fmt(m.accounts) },
              ]}
              rows={modes.map((m) => ({ ...m, id: m.mode }))}
            />
            {autoUnrouted ? (
              <p style={{ padding: "8px 20px 16px", fontSize: 12, color: "var(--aa-muted)" }}>
                {fmt(autoUnrouted)} Auto {autoUnrouted === 1 ? "message was" : "messages were"} sent before Auto had run any model in its
                conversation, so {autoUnrouted === 1 ? "it counts" : "they count"} here and not on a model below.
              </p>
            ) : null}
          </>
        ) : (
          <Empty icon="sparkle" title="No messages in this period" />
        )}
      </Card>

      {/* ── Which models run ───────────────────────────────────────────── */}
      <Card
        title="Which models run"
        subtitle="Every message, by the model it ran on: the one the user picked, or the one Auto ran for it. Full history."
        flush
      >
        {loading ? (
          <RowsSkeleton rows={4} />
        ) : picked.length ? (
          <>
            <div style={{ padding: "16px 20px 4px" }}>
              <StackedBars rows={daily} series={series} height={220} />
            </div>
            <Table
              defaultSort={{ key: "messages", dir: "desc" }}
              columns={[
                { key: "label", label: "Model", render: (p) => <ModelName pin={p.pin} label={p.label} /> },
                { key: "messages", label: "Messages", align: "right", render: (p) => fmt(p.messages) },
                { key: "share", label: "Share", align: "right", render: (p) => pct(p.share, 1) },
                { key: "manual", label: "Picked by the user", align: "right", render: (p) => fmt(p.manual) },
                { key: "auto", label: "By Auto", align: "right", render: (p) => fmt(p.auto) },
                {
                  key: "who",
                  label: "Who picked it",
                  sortable: false,
                  width: 140,
                  render: (p) => <WhoPicked manual={p.manual} auto={p.auto} />,
                },
                {
                  key: "conversations",
                  label: "Conversations",
                  align: "right",
                  render: (p) => <Count n={p.conversations} onClick={() => open("pin", { pin: p.pin }, `${p.label} · conversations`)} />,
                },
                { key: "users", label: "People", align: "right", render: (p) => fmt(p.users) },
                { key: "accounts", label: "Workspaces", align: "right", render: (p) => fmt(p.accounts) },
              ]}
              rows={picked.map((p) => ({ ...p, id: p.pin }))}
            />
          </>
        ) : (
          <Empty icon="sparkle" title="No messages in this period" />
        )}
      </Card>

      {/* ── Does each model find results ───────────────────────────────── */}
      <Card
        title="Does each model find results"
        subtitle="Of every search a model ran, how many came back with a list, how big the lists are, what they cost and how the turns were judged."
        flush
      >
        {loading ? (
          <RowsSkeleton rows={4} />
        ) : models.some((m) => m.searches || m.previews || m.judged.total) ? (
          <Table
            defaultSort={{ key: "searches", dir: "desc" }}
            columns={[
              {
                key: "label",
                label: "Model",
                render: (m) => (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <ModelName pin={m.id} label={m.label} />
                    <Pill tone={m.kind === "filters" ? "neutral" : "accent"}>{m.kind === "filters" ? "Filters" : "Research"}</Pill>
                  </span>
                ),
              },
              {
                key: "searches",
                label: "Searches",
                align: "right",
                render: (m) => <span title={`${pct(m.searches / Math.max(1, totalSearches))} of searches`}>{fmt(m.searches)}</span>,
              },
              {
                key: "resultRate",
                label: "Found results",
                align: "right",
                render: (m) => (
                  <button type="button" className="aa-link" disabled={!m.found} onClick={() => open("found", { source: m.id }, `${m.label} · found results`)}>
                    <Rate value={m.resultRate} status={statusOf(m.resultRate, { good: 0.8, bad: 0.5 })} />
                  </button>
                ),
              },
              {
                key: "zeroRate",
                label: "Found nothing",
                align: "right",
                render: (m) => (
                  <button type="button" className="aa-link" disabled={!m.zeroRate} onClick={() => open("zero", { source: m.id }, `${m.label} · found nothing`)}>
                    <Rate value={m.zeroRate} status={statusOf(m.zeroRate, { good: 0.05, bad: 0.2, higherIsBetter: false })} />
                  </button>
                ),
              },
              { key: "failed", label: "Failed", align: "right", render: (m) => fmt(m.failed) },
              {
                key: "lists",
                label: "Lists · who picked",
                align: "right",
                sort: (m) => m.lists?.total ?? 0,
                render: (m) =>
                  m.lists?.total ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                      {fmt(m.lists.total)}
                      <WhoPicked manual={m.lists.manual} auto={m.lists.auto} width={60} />
                    </span>
                  ) : (
                    <span style={{ color: "var(--aa-faint)" }}>0</span>
                  ),
              },
              {
                key: "byMode",
                label: "Found results · user / Auto",
                align: "right",
                sortable: false,
                render: (m) => <ModeRates byMode={m.byMode} onOpen={(mode) => open("found", { source: m.id, mode }, `${m.label} · ${MODE_SERIES.find((x) => x.key === mode).label} · found results`)} />,
              },
              {
                key: "sizes",
                label: "List sizes",
                sortable: false,
                width: 180,
                render: (m) =>
                  m.sizes ? (
                    <span title={buckets.map((b) => `${b.label}: ${fmt(m.sizes[b.key] ?? 0)}`).join(" · ")}>
                      <SegmentBar segments={buckets.map((b, i) => ({ label: b.label, value: m.sizes[b.key] ?? 0, color: `var(--aa-s${i + 1})` }))} />
                    </span>
                  ) : (
                    <span style={{ color: "var(--aa-faint)" }}>—</span>
                  ),
              },
              { key: "medianFound", label: "Typical size", align: "right", render: (m) => fmt(m.medianFound) },
              { key: "creditsPerSearch", label: "Credits / search", align: "right", render: (m) => (m.creditsPerSearch == null ? "—" : fmt(m.creditsPerSearch, 1)) },
              {
                key: "score",
                label: "Did what they asked",
                align: "right",
                sort: (m) => m.judged.score ?? -1,
                render: (m) => (
                  <button type="button" className="aa-link" disabled={!m.judged.total} onClick={() => open("outcome", { agent_model: m.id }, `${m.label} · judged turns`)}>
                    <Rate value={m.judged.score} status={statusOf(m.judged.score, { good: 0.75, bad: 0.5 })} />
                  </button>
                ),
              },
            ]}
            rows={models.map((m) => ({ ...m, id: m.id }))}
          />
        ) : (
          <Empty icon="sparkle" title="No searches in this period">
            Rows fill in as searches run on each model.
          </Empty>
        )}
        {buckets.length ? (
          <div style={{ display: "flex", gap: 14, padding: "10px 20px 16px", fontSize: 12, color: "var(--aa-muted)" }}>
            List sizes:
            {buckets.map((b, i) => (
              <span key={b.key} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: `var(--aa-s${i + 1})` }} />
                {b.label}
              </span>
            ))}
          </div>
        ) : null}
      </Card>

      {/* ── Model changes ──────────────────────────────────────────────── */}
      <Card
        title="Model changes"
        subtitle="A change is the picker changing between two messages in a conversation: to or from Auto, or to another model the user picked. Auto routing to another model is not a change. Full history."
        flush
      >
        {loading ? (
          <RowsSkeleton rows={4} />
        ) : changes && changes.conversations ? (
          <>
            <div className="aa-grid aa-grid-kpi" style={{ padding: "16px 20px 0" }}>
              <Kpi
                label="Conversations that changed model"
                value={pct(changes.rate, 1)}
                foot={`${fmt(changes.switchingConversations)} of ${fmt(changes.conversations)} with 2+ messages`}
              />
              <Kpi
                label="Changes"
                value={fmt(changes.switches)}
                foot={changes.perSwitchingConversation ? `${fmt(changes.perSwitchingConversation, 1)} per changing conversation · most ${fmt(changes.most)}` : "—"}
              />
              <Kpi
                label="New model found results"
                value={pct(changes.foundAfterRate)}
                status={statusOf(changes.foundAfterRate, { good: 0.6, bad: 0.3 })}
                foot={`${fmt(changes.foundAfter)} of ${fmt(changes.switches)} changes`}
              />
              <Kpi label="Changed after results" value={fmt(changes.afterResults)} foot={`${fmt(changes.switches - changes.afterResults)} before any results`} />
            </div>
            {changes.routes.length ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, padding: "16px 20px" }}>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Most changed to</p>
                    <BarList rows={changes.to.map((r) => ({ key: r.pin, label: r.label, value: r.count }))} color="var(--aa-good)" />
                  </div>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Most changed away from</p>
                    <BarList rows={changes.from.map((r) => ({ key: r.pin, label: r.label, value: r.count }))} color="var(--aa-warn)" />
                  </div>
                </div>
                <Table
                  defaultSort={{ key: "count", dir: "desc" }}
                  columns={[
                    {
                      key: "route",
                      label: "From → to",
                      sort: (r) => `${r.fromLabel} ${r.toLabel}`,
                      render: (r) => <Route from={r.from} fromLabel={r.fromLabel} to={r.to} toLabel={r.toLabel} />,
                    },
                    {
                      key: "count",
                      label: "Changes",
                      align: "right",
                      render: (r) => <Count n={r.count} onClick={() => open("switch", { from: r.from, to: r.to }, `${r.fromLabel} → ${r.toLabel} · changes`)} />,
                    },
                    { key: "afterResults", label: "After results", align: "right", render: (r) => fmt(r.afterResults) },
                    { key: "before", label: "Before any results", align: "right", sort: (r) => r.count - r.afterResults, render: (r) => fmt(r.count - r.afterResults) },
                    {
                      key: "foundAfterRate",
                      label: "Found results after",
                      align: "right",
                      render: (r) => <Rate value={r.foundAfterRate} status={statusOf(r.foundAfterRate, { good: 0.6, bad: 0.3 })} />,
                    },
                  ]}
                  rows={changes.routes.map((r) => ({ ...r, id: `${r.from}-${r.to}` }))}
                />
              </>
            ) : (
              <Empty compact icon="sparkle" title="Nobody changed model in this period" />
            )}
          </>
        ) : (
          <Empty compact icon="sparkle" title="No conversations with two or more messages yet" />
        )}
      </Card>

      {/* ── Auto ────────────────────────────────────────────────────── */}
      <Card title="How Auto routes" subtitle="The model Auto picked for each search, and why — from the deploy that records it." flush>
        {loading ? (
          <RowsSkeleton rows={3} />
        ) : routing.length ? (
          <Table
            defaultSort={{ key: "total", dir: "desc" }}
            columns={[
              { key: "label", label: "Picked", render: (r) => (r.to === "ask" ? r.label : <ModelName pin={r.to} label={r.label} />) },
              {
                key: "total",
                label: "Searches",
                align: "right",
                render: (r) => <Count n={r.total} onClick={() => open("routed", { to: r.to }, `Auto → ${r.label}`)} />,
              },
              {
                key: "rules",
                label: "Why",
                sortable: false,
                render: (r) =>
                  Object.entries(r.rules)
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, n]) => `${RULE_LABELS[k] ?? words(k)} ${fmt(n)}`)
                    .join(" · "),
              },
            ]}
            rows={routing.map((r) => ({ ...r, id: r.to }))}
          />
        ) : (
          <Empty compact icon="sparkle" title="Auto hasn't routed a search in this period" />
        )}
      </Card>

      {/* ── Suggestions ────────────────────────────────────────────────── */}
      <Card
        title="Suggestions"
        subtitle={suggested ? `${fmt(suggested)} made · ${fmt(accepted)} taken (${pct(accepted / suggested)})` : "When another model could do better, we offer it"}
        flush
      >
        {loading ? (
          <RowsSkeleton rows={3} />
        ) : suggestions.length ? (
          <Table
            defaultSort={{ key: "suggested", dir: "desc" }}
            columns={[
              { key: "route", label: "From → to", sort: (r) => `${r.fromLabel} ${r.toLabel}`, render: (r) => `${r.fromLabel} → ${r.toLabel}` },
              { key: "suggested", label: "Offered", align: "right", render: (r) => <Count n={r.suggested} onClick={() => open("suggested", { from: r.from, to: r.to }, `${r.fromLabel} → ${r.toLabel} · offered`)} /> },
              { key: "accepted", label: "Taken", align: "right", render: (r) => <Count n={r.accepted} onClick={() => open("accepted", { from: r.from, to: r.to }, `${r.fromLabel} → ${r.toLabel} · taken`)} /> },
              { key: "declined", label: "Declined", align: "right", render: (r) => <Count n={r.declined} onClick={() => open("declined", { from: r.from, to: r.to }, `${r.fromLabel} → ${r.toLabel} · declined`)} /> },
              { key: "acceptRate", label: "Taken rate", align: "right", render: (r) => <Rate value={r.acceptRate} status={statusOf(r.acceptRate, { good: 0.5, bad: 0.2 })} /> },
              {
                key: "at",
                label: "Where offered",
                sortable: false,
                render: (r) =>
                  Object.entries(r.at)
                    .map(([k, n]) => `${AT_LABELS[k] ?? words(k)} ${fmt(n)}`)
                    .join(" · ") || "—",
              },
            ]}
            rows={suggestions.map((r) => ({ ...r, id: `${r.from}-${r.to}` }))}
          />
        ) : (
          <Empty compact icon="sparkle" title="No suggestions yet">
            One appears when a request asks for something the current model can't search, or a search finds nothing.
          </Empty>
        )}
      </Card>

      <Card
        title="Switches the router started"
        subtitle="Model changes the server made on a request — from the picker, or a suggestion taken — with who picks the model on each side. Under Auto a suggestion taken stays in Auto. From the deploy that records them."
        flush
      >
        {loading ? (
          <RowsSkeleton rows={3} />
        ) : switches.length ? (
          <Table
            defaultSort={{ key: "total", dir: "desc" }}
            columns={[
              {
                key: "route",
                label: "From → to",
                sort: (r) => `${r.fromLabel} ${r.toLabel}`,
                render: (r) => <Route from={r.from} fromLabel={r.fromLabel} to={r.to} toLabel={r.toLabel} />,
              },
              { key: "total", label: "Switches", align: "right", render: (r) => <Count n={r.total} onClick={() => open("switched", switchOf(r), `${r.fromLabel} → ${r.toLabel} · switches`)} /> },
              { key: "dropdown", label: "By hand", align: "right", render: (r) => <Count n={r.dropdown} onClick={() => open("switched", { ...switchOf(r), via: "dropdown" }, `${r.fromLabel} → ${r.toLabel} · by hand`)} /> },
              { key: "suggestion", label: "From a suggestion", align: "right", render: (r) => <Count n={r.suggestion} onClick={() => open("switched", { ...switchOf(r), via: "suggestion" }, `${r.fromLabel} → ${r.toLabel} · from a suggestion`)} /> },
            ]}
            rows={switches.map((r) => ({ ...r, id: `${r.fromMode}:${r.from}-${r.toMode}:${r.to}` }))}
          />
        ) : (
          <Empty compact icon="sparkle" title="No switches recorded yet" />
        )}
      </Card>

    </div>
  );
}

/** Every UTC day in the window with one count per model, the empty days included. */
function fillPickedDays(daily, from, to) {
  if (!from || !to) return [];
  const byDay = new Map((daily ?? []).map((d) => [d.day, d]));
  const out = [];
  for (let t = dayStart(toDay(from)); t < Date.parse(to); t += DAY) {
    const key = toDay(t);
    const { day: _day, ...pins } = byDay.get(key) ?? {};
    const date = new Date(t);
    out.push({
      key,
      ...pins,
      label: date.toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" }),
      long: date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }),
    });
  }
  return out;
}

/** A group of tiles' name, with what it counts. */
function GroupLabel({ title, hint }) {
  return (
    <p style={{ fontSize: 12, margin: 0, color: "var(--aa-muted)" }}>
      <span style={{ fontWeight: 600, color: "var(--aa-ink)", marginRight: 8 }}>{title}</span>
      {hint}
    </p>
  );
}

/** A switch row's drill-down labels: both models, and who picked each (the metric's `from_mode` / `to_mode`). */
function switchOf(r) {
  return { from: r.from, to: r.to, ...(r.fromMode ? { from_mode: r.fromMode } : {}), ...(r.toMode ? { to_mode: r.toMode } : {}) };
}

/** A mode's name with its chart colour. */
function ModeName({ mode, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: MODE_COLORS[mode] ?? "var(--aa-neutral)", flexShrink: 0 }} />
      {label}
    </span>
  );
}

/** How many the user picked vs Auto ran, as one bar. */
function WhoPicked({ manual = 0, auto = 0, width = 120 }) {
  if (!manual && !auto) return <span style={{ color: "var(--aa-faint)" }}>—</span>;
  return (
    <span style={{ display: "inline-block", width }} title={`Picked by the user ${fmt(manual)} · by Auto ${fmt(auto)}`}>
      <SegmentBar
        segments={[
          { label: "Picked by the user", value: manual, color: MODE_COLORS.manual },
          { label: "Auto", value: auto, color: MODE_COLORS.auto },
        ]}
      />
    </span>
  );
}

/** A model's found-results rate under each mode, from the signals that carry one. */
function ModeRates({ byMode, onOpen }) {
  if (!byMode) return <span style={{ color: "var(--aa-faint)" }}>—</span>;
  const rate = (mode) => {
    const m = byMode[mode];
    if (!m?.searches) return <span style={{ color: "var(--aa-faint)" }}>—</span>;
    const value = m.found / m.searches;
    return (
      <button type="button" className="aa-link" disabled={!m.found} onClick={() => onOpen(mode)} title={`${fmt(m.found)} of ${fmt(m.searches)} searches`}>
        <Rate value={value} status={statusOf(value, { good: 0.8, bad: 0.5 })} />
      </button>
    );
  };
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
      {rate("manual")}
      <span style={{ color: "var(--aa-faint)" }}>/</span>
      {rate("auto")}
    </span>
  );
}

/** One side → the other, each with its model's colour. */
function Route({ from, fromLabel, to, toLabel }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <ModelName pin={from} label={fromLabel} />
      <span style={{ color: "var(--aa-faint)" }}>→</span>
      <ModelName pin={to} label={toLabel} />
    </span>
  );
}

/** A model's name with its chart colour. */
function ModelName({ pin, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: colorOf(pin), flexShrink: 0 }} />
      {label}
    </span>
  );
}

/** A count that opens its conversations; a zero is just a zero. */
function Count({ n, onClick }) {
  if (!n) return <span style={{ color: "var(--aa-faint)" }}>0</span>;
  return (
    <button type="button" className="aa-link" onClick={onClick}>
      {fmt(n)}
    </button>
  );
}
